import { crewDataNA, EB_OPERATOR_LABEL, RRO_OPERATOR_LABEL } from 'constants/crewDetails';
import { isTimestampBefore, minutesDuration } from 'utils/datetime';

const NA = 'N/A';

const EMPLOYMENT_LABEL = {
	fullTime: 'F',
	partTime: 'P',
};

/**
 * Reduce `currentEmploymentType` (an object with an `employmentType` key,
 * or `null` / `undefined`) to its single-letter chip label.
 *
 * @param {{ employmentType?: string } | null | undefined} currentEmploymentType
 * @returns {string}
 */
export const deriveEmploymentTypeLabel = (currentEmploymentType) =>
	currentEmploymentType ? EMPLOYMENT_LABEL[currentEmploymentType.employmentType] : NA;

/**
 * Reduce `currentRun` (an object, `null`, or `undefined`) to the driver's
 * classification label.
 *   - `undefined`  → generic "N/A" (backend truly did not send anything).
 *   - `null`       → EB operator (backend explicitly said "no run").
 *   - object       → RRO operator (assigned to a specific run).
 *
 * @param {object | null | undefined} currentRun
 * @returns {string}
 */
export const deriveClassificationTypeLabel = (currentRun) => {
	if (currentRun === undefined) return crewDataNA;
	if (currentRun === null) return EB_OPERATOR_LABEL;
	return RRO_OPERATOR_LABEL;
};

/**
 * Combine the employment-type letter (`F` / `P` / `N/A`) with the
 * classification (`RRO` / `EB` / `N/A`) into a single chip caption.
 *
 * @param {string} employmentTypeLabel
 * @param {string} classificationTypeLabel
 * @returns {string}
 */
export const getCombinedLabel = (employmentTypeLabel, classificationTypeLabel) => {
	const hasEmployment = employmentTypeLabel && employmentTypeLabel !== NA;
	const hasClassification = classificationTypeLabel && classificationTypeLabel !== NA;

	if (hasEmployment && hasClassification) {
		return `${classificationTypeLabel}/${employmentTypeLabel}`;
	}
	if (hasEmployment) return employmentTypeLabel;
	if (hasClassification) return classificationTypeLabel;
	return NA;
};

/**
 * Format the capacity chip: `"<remaining>m<rem-mins> (of <max>m<max-mins>)"`
 * when `max !== 0`; otherwise `"N/A"`.
 *
 * @param {number} remaining
 * @param {number} max
 * @returns {string}
 */
export const buildCapacityLabel = (remaining, max) => {
	if (max === 0) return NA;
	return `${minutesDuration(remaining)} (of ${minutesDuration(max)})`;
};

/**
 * Pick between the "already arrived" and the "expected arrival" caption
 * based on whether `nextAvailability` has already passed.
 *
 * @param {string | null} nextAvailability UTC ISO
 * @param {string} now UTC ISO
 * @param {{ arrivedAtLocation: string, expectedPresence: string }} texts
 * @returns {string}
 */
export const derivePresenceLabel = (nextAvailability, now, texts) => {
	if (nextAvailability !== null && isTimestampBefore(nextAvailability, now)) {
		return texts.arrivedAtLocation;
	}
	return texts.expectedPresence;
};

/**
 * True iff the given `now` timestamp falls inside at least one active
 * hold-down window from `holdDowns`.
 *
 * @param {Array<{ effectiveTimestamp: string, expireTimestamp: string }> | undefined} holdDowns
 * @param {string} now UTC ISO
 * @returns {boolean}
 */
export const hasActiveHoldDownAt = (holdDowns, now) =>
	Boolean(
		holdDowns?.some(
			(holdDown) =>
				isTimestampBefore(holdDown.effectiveTimestamp, now) &&
				isTimestampBefore(now, holdDown.expireTimestamp)
		)
	);

/**
 * "Run: <name>" or "Run: N/A" when the driver has no active run.
 * @param {{ name?: string } | null | undefined} currentRun
 * @returns {string}
 */
export const buildRunLabel = (currentRun) =>
	currentRun !== null && currentRun !== undefined ? `Run: ${currentRun.name}` : 'Run: N/A';

/**
 * "Jobs: <comma-joined codes>" or "Job(s): N/A" when there are no jobs.
 * @param {Array<{ code: string }> | null | undefined} currentJobs
 * @returns {string}
 */
export const buildJobLabel = (currentJobs) =>
	currentJobs && currentJobs.length > 0
		? `Jobs: ${currentJobs.map((job) => job.code).join(',')}`
		: 'Job(s): N/A';
